import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import Header from '../Header';

const User = () => {
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [isGSTValid, setIsGSTValid] = useState(true);
  const [isAmountValid, setIsAmountValid] = useState(true); // New state

  const [user, setUser] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    reg_gst: '',
    gst_number: '',
    service: '',
    plan: '',
    customPlan: '',
    totalAmount: '',
    remarks: '',
    dor: '',
  });

  useEffect(() => {
    fetch('https://apiaksharaconnect.aksharafinalytics.in/getservices')
      .then(response => response.json())
      .then(data => setServices(data || []))
      .catch(() => setError('Error fetching services.'));
  }, []);

  const handleServiceChange = async (e) => {
    const selectedService = e.target.value;
    setUser(prev => ({ ...prev, service: selectedService, plan: '', customPlan: '', totalAmount: '' }));
    if (selectedService) {
      setLoadingPlans(true);
      try {
        const response = await fetch(`https://apiaksharaconnect.aksharafinalytics.in/getplans/${selectedService}`);
        const data = await response.json();
        setPlans(data || []);
      } catch {
        setError('Error fetching plans for the selected service.');
      } finally {
        setLoadingPlans(false);
      }
    } else {
      setPlans([]);
    }
  };

  const validateGST = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst.toUpperCase());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'reg_gst' && value === 'no') {
      setUser(prev => ({ ...prev, [name]: value, gst_number: '' }));
    } else if (name === 'plan') {
      setUser(prev => ({
        ...prev,
        [name]: value,
        customPlan: value === 'Others' ? prev.customPlan : '',
        totalAmount: '' // Reset totalAmount on plan change
      }));
      setIsAmountValid(true); // Reset amount validation
      return;
    } else {
      setUser(prev => ({ ...prev, [name]: value }));
    }

    if (name === 'gst_number') {
      setIsGSTValid(value === '' || validateGST(value));
    }

    if (name === 'totalAmount') {
      const selectedPlan = plans.find(plan => plan.id === user.plan);
      const maxAmount = selectedPlan ? Number(selectedPlan.price) : null;

      if (maxAmount !== null && Number(value) > maxAmount) {
        setIsAmountValid(false);
        setError(`Total amount cannot exceed ₹${maxAmount}`);
      } else {
        setIsAmountValid(true);
        setError(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (user.reg_gst === 'yes' && !validateGST(user.gst_number)) {
      setIsGSTValid(false);
      setError('Invalid GST Number. Please check and try again.');
      return;
    }

    const selectedPlan = plans.find(plan => plan.id === user.plan);
    const maxAmount = selectedPlan ? Number(selectedPlan.price) : null;

    if (maxAmount !== null && Number(user.totalAmount) > maxAmount) {
      setIsAmountValid(false);
      setError(`Total amount cannot exceed ₹${maxAmount}`);
      return;
    }

    setLoading(true);
    const userData = {
      ...user,
      plan: user.plan === 'Others' ? user.customPlan : user.plan,
    };

    try {
      const response = await fetch('https://apiaksharaconnect.aksharafinalytics.in/user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || response.statusText);

      if (data.message) {
        setSuccessMessage('User successfully added!');
        setUser({
          company: '',
          name: '',
          email: '',
          phone: '',
          address: '',
          reg_gst: '',
          gst_number: '',
          service: '',
          plan: '',
          customPlan: '',
          totalAmount: '',
          remarks: '',
          dor: '',
        });
      } else if (data.error) {
        setError(data.error);
      }
    } catch {
      setError('There was an error submitting the user. Please try again.');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 5000);
    }
  };

  return (
    <>
      <Header />

      <Container className="mt-5">
        {successMessage && <Alert variant="success">{successMessage}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="shadow">
          <Card.Header>
            <h2>
              Add User{' '}
              <Button variant="primary" onClick={() => navigate('/Userlist')}>
                User List
              </Button>
            </h2>
          </Card.Header>

          <Card.Body>
            {loading ? (
              <div className="text-center my-5">
                <Spinner animation="border" role="status" />
                <div className="mt-3">Submitting ...</div>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Company / Business Name</Form.Label>
                    <Form.Control type="text" name="company" value={user.company} onChange={handleChange} required />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label>Authorized Name</Form.Label>
                    <Form.Control type="text" name="name" value={user.name} onChange={handleChange} required />
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control type="email" name="email" value={user.email} onChange={handleChange} required />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control type="tel" name="phone" value={user.phone} onChange={handleChange} required />
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control type="text" name="address" value={user.address} onChange={handleChange} />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Label>Are you GST registered?</Form.Label>
                    <Form.Control as="select" name="reg_gst" value={user.reg_gst} onChange={handleChange} required>
                      <option value="">-- Select Option --</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </Form.Control>
                  </Col>

                  {user.reg_gst === 'yes' && (
                    <Col md={6} className="mb-3">
                      <Form.Label>GST Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="gst_number"
                        value={user.gst_number}
                        onChange={handleChange}
                        className={!isGSTValid ? 'is-invalid' : ''}
                      />
                      {!isGSTValid && <div className="invalid-feedback">Please enter a valid GST number.</div>}
                    </Col>
                  )}
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Service</Form.Label>
                    <Form.Control as="select" name="service" value={user.service} onChange={handleServiceChange} required>
                      <option value="">-- Select Service --</option>
                      {loadingPlans ? (
                        <option disabled>Loading...</option>
                      ) : services.length > 0 ? (
                        services.map(service => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No services available</option>
                      )}
                    </Form.Control>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Label>Plan</Form.Label>
                    <Form.Control as="select" name="plan" value={user.plan} onChange={handleChange} required>
                      <option value="">-- Select Plan --</option>
                      {loadingPlans ? (
                        <option disabled>Loading...</option>
                      ) : plans.length > 0 ? (
                        plans.map(plan => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} - {plan.price}/-
                          </option>
                        ))
                      ) : (
                        <option disabled>No plans available</option>
                      )}
                    </Form.Control>
                  </Col>

                  {(user.plan === '42' || user.plan === '43') && (
                    <Col md={6} className="mb-3">
                      <Form.Label>Others </Form.Label>
                      <Form.Control type="text" name="customPlan" value={user.customPlan} onChange={handleChange} required />
                    </Col>
                  )}
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>Total Amount</Form.Label>
                    <Form.Control
                      type="number"
                      name="totalAmount"
                      value={user.totalAmount}
                      onChange={handleChange}
                      className={!isAmountValid ? 'is-invalid' : ''}
                      required
                    />
                    {!isAmountValid && (
                      <div className="invalid-feedback">
                        {error || 'Total amount is invalid.'}
                      </div>
                    )}
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control type="text" name="remarks" value={user.remarks} onChange={handleChange} required />
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Label>Date</Form.Label>
                    <Form.Control type="date" name="dor" value={user.dor} onChange={handleChange} required />
                  </Col>
                </Row>

                <div className="text-center">
                  <Button type="submit" variant="primary">
                    Submit
                  </Button>
                </div>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default User;
